import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PassportView } from "@/components/PassportView";
import { InfoTip } from "@/components/InfoTip";
import { JobCompare } from "@/components/JobCompare";
import { Pencil, Loader2, X, Check, Briefcase, Power, UploadCloud } from "lucide-react";

export default function CandidateProfile() {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/candidate/profile").then((r) => setP(r.data)).catch(() => nav("/app/upload")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = () => {
    setForm({ name: p.name, title: p.title, location: p.location || "", about: p.about || "", availability: p.availability || "" });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/candidate/profile", form);
      setP(data);
      setEditing(false);
      toast.success("Profil uložený");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-700" /></div>;
  if (!p) return null;

  return (
    <div className="px-6 pt-12">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight">Môj profil</h1>
        <div className="flex items-center gap-2">
          <ReuploadCV onDone={(data) => setP(data)} />
          <button data-testid="edit-profile-btn" onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <Pencil size={15} /> Upraviť
          </button>
        </div>
      </div>

      <JobCompare base="/candidate" jobsUrl="/candidate/compare/jobs" careerScore={p.career_score} />
      <PassportView p={p} showInsights />

      <JobPreferences profile={p} />

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setEditing(false)}>
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-head text-xl font-bold">Upraviť profil</h2>
              <button onClick={() => setEditing(false)} className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <EditField label="Meno" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="edit-name" />
              <EditField label="Pozícia" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="edit-title" />
              <EditField label="Lokalita" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="edit-location" />
              <EditField label="Dostupnosť" value={form.availability} onChange={(v) => setForm({ ...form, availability: v })} testId="edit-availability" />
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">O mne</span>
                <textarea data-testid="edit-about" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} rows={4}
                  className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
              </label>
              <button data-testid="save-profile-btn" onClick={save} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Uložiť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobPreferences({ profile }) {
  const [open, setOpen] = useState(!!profile.open_to_work);
  const [selected, setSelected] = useState(profile.interest_areas || []);
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/candidate/interest-areas").then((r) => setAreas(r.data.areas || [])).catch(() => {}); }, []);

  const toggleArea = (a) => setSelected((s) => s.includes(a) ? s.filter((x) => x !== a) : [...s, a]);

  const save = async (nextOpen, nextAreas) => {
    setSaving(true);
    try {
      await api.put("/candidate/preferences", { open_to_work: nextOpen, interest_areas: nextAreas });
      toast.success("Preferencie uložené");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onToggleOpen = () => { const v = !open; setOpen(v); save(v, selected); };
  const onSaveAreas = () => save(open, selected);

  return (
    <div className="mt-5 rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid="job-preferences-card">
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-2xl bg-blue-700 flex items-center justify-center"><Briefcase className="text-white" size={18} /></div>
        <h2 className="font-head font-semibold text-lg">Pracovné preferencie</h2>
      </div>

      <label className="mt-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4 cursor-pointer" data-testid="open-to-work-toggle">
        <span className="flex items-center gap-2"><Power size={17} className={open ? "text-emerald-600" : "text-slate-400"} />
          <span className="text-sm font-semibold text-slate-800">Som otvorený pracovným príležitostiam</span>
          <InfoTip text={'Keď je zapnuté, v sekcii „Ponuky pre teba" uvidíte verejné pracovné ponuky.'} />
        </span>
        <input type="checkbox" checked={open} onChange={onToggleOpen} disabled={saving} className="h-5 w-5 accent-blue-700" />
      </label>

      <div className="mt-4">
        <span className="text-sm font-semibold text-slate-700">Oblasti pracovného záujmu</span>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="interest-areas">
          {areas.map((a) => (
            <button key={a} data-testid={`interest-${a}`} onClick={() => toggleArea(a)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${selected.includes(a) ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {a}
            </button>
          ))}
        </div>
        <button data-testid="save-interests-btn" onClick={onSaveAreas} disabled={saving}
          className="mt-4 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Uložiť oblasti
        </button>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
    </label>
  );
}

function ReuploadCV({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Nahrať nové CV? Profil sa aktualizuje podľa nového CV (AI analýza, Career Score).")) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/candidate/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data);
      toast.success("Profil bol aktualizovaný podľa nového CV");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Nahratie CV zlyhalo");
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={upload} data-testid="reupload-cv-input" />
      <button data-testid="reupload-cv-btn" onClick={() => ref.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {busy ? "Analyzujem…" : "Nové CV"}
      </button>
    </>
  );
}
